import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

type Language = 'en' | 'fr';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
  getLocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  
  // Initialize with URL language or stored language
  const initializeLanguage = (): Language => {
    if (lang && ['en', 'fr'].includes(lang)) {
      return lang as Language;
    }
    const storedLanguage = localStorage.getItem('language') as Language;
    return storedLanguage || 'en';
  };
  
  const [currentLanguage, setCurrentLanguage] = useState<Language>(initializeLanguage);
  
  // Apply language change
  const changeLanguage = useCallback((language: Language) => {
    localStorage.setItem('language', language);
    i18n.changeLanguage(language);
    setCurrentLanguage(language);
    
    // Update URL to reflect language change
    const pathWithoutLang = location.pathname.replace(/^\/(en|fr)/, '');
    const newPath = pathWithoutLang === '' ? `/${language}` : `/${language}${pathWithoutLang}`;
    navigate(newPath + location.search + location.hash);
  }, [i18n, location, navigate]);
  
  // Get path with language prefix
  const getLocalizedPath = (path: string): string => {
    const cleanPath = path.replace(/^\/(en|fr)/, '').replace(/^\//, '');
    return cleanPath === '' ? `/${currentLanguage}` : `/${currentLanguage}/${cleanPath}`;
  };
  
  // Update language when URL changes
  useEffect(() => {
    if (lang && ['en', 'fr'].includes(lang) && lang !== currentLanguage) {
      changeLanguage(lang as Language);
    }
  }, [lang, currentLanguage, changeLanguage]);
  
  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};